import React, { useEffect, useState } from 'react';
import ProjectCard from '../components/molecules/ProjectCard';
import { motion, AnimatePresence } from 'framer-motion';
import projectsData from '../projects_clean.json';
import { useLocation } from 'react-router-dom';
import FeedbackModal from '../components/molecules/FeedbackModal';
import FloatingButton from '../components/molecules/FloatingButton';

const formatTime = (time) => {
  if (time <= 24) {
    return `${time} hrs`;
  } else if (time <= 36) {
    return `1 Day`;
  } else {
    const days = Math.round(time / 24);
    return `${days} Days`;
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.5,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const location = useLocation();
  const { levelId } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const pageName = "ProjectDisplay";
  const projecttitle = "Projects";
  const openFeedbackModal = () => setIsFeedbackModalOpen(true);
  const closeFeedbackModal = () => setIsFeedbackModalOpen(false);

  const loadProjects = () => {
    try {
      if (levelId) {
        // Filter projects by level
        const levelProjects = projectsData.filter(project => project.level_id === levelId);
        setProjects(levelProjects);
        setFilteredProjects(levelProjects);
      } else {
        // Show all projects if no level filter
        setProjects(projectsData);
        setFilteredProjects(projectsData);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(`Failed to load projects: ${err.message}`);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [levelId]);

  return (
    <div className="bg-gradient-to-b from-[#232320] via-[#2C3E50] to-[#232320] text-[#E0E0E0] min-h-screen py-10 content-container">
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-6xl md:text-7xl font-display mb-6 text-[#FEF158]">
            {`Level ${levelId || 'Projects'}`}
          </h1>
          <h3 className="text-2xl md:text-3xl text-[#E0E0E0] font-normalText">
            No more theory, time to build something dope.
          </h3>
        </div>
        {loading ? (
          <p className="text-center text-xl">Loading...</p>
        ) : error ? (
          <p className="text-center text-xl text-red-500">{error}</p>
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16"
            >
              <AnimatePresence>
                {projects.map((project) => (
                  <motion.div
                    key={project.id}
                    variants={cardVariants}
                  >
                    <ProjectCard project={{
                      id: project.id,
                      lvl: project.level_id,
                      title: project.title,
                      oneliner: project.one_liner,
                      time: formatTime(project.estimated_time),
                      image: project.thumbnail_url
                    }} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
            
            <div className="text-center mt-12">
              <span className="text-[#E0E0E0] text-lg">{`Showing ${projects.length} projects`}</span>
            </div>
          </>
        )}
      </div>
      <FeedbackModal isOpen={isFeedbackModalOpen} onClose={closeFeedbackModal} pageName={pageName} projecttitle={projecttitle} />
      <FloatingButton onClick={openFeedbackModal} isFeedbackModalOpen={isFeedbackModalOpen} />
    </div>
  );
};

export default ProjectsPage;